import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { anyBlockingModalOpenAtom, showOtherModalsAtom } from 'src/store';

// Shared with the EJS homepage nag (website/js/dealsNag.js) — same key + shape so
// dismissing on one surface suppresses it on the others.
const LS_KEY = 'dealsPrompt';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// Deals disabled.
const DISABLED = true;

type DealsNagState = {
  dismissedForever?: boolean;
  lastShownAt?: number;
};

function readState(): DealsNagState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as DealsNagState) : {};
  } catch {
    return {};
  }
}

function writeState(state: DealsNagState) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* localStorage unavailable (private mode / quota) — fail silently */
  }
}

/**
 * Returns true if the nag is eligible to show right now, recording the show
 * timestamp when it is. Shows at most once every 30 days; never after the user
 * picked "Don't Show Again".
 */
function shouldShowAndRecord(): boolean {
  const state = readState();
  if (state.dismissedForever) return false;

  const now = Date.now();
  if (!state.lastShownAt || now - state.lastShownAt > THIRTY_DAYS_MS) {
    writeState({ ...state, lastShownAt: now });
    return true;
  }
  return false;
}

/** "Don't Show Again" — hide the nag permanently. */
export function markDealsNagDismissedForever() {
  writeState({ ...readState(), dismissedForever: true });
}

type Props = {
  setIsOpen: (open: boolean) => void;
  /** CartHistory only: defer to the WelcomeModal control atom before showing. */
  deferToWelcomeModal?: boolean;
};

export const useShowDealsNag = ({ setIsOpen, deferToWelcomeModal }: Props) => {
  const anyBlockingModalOpen = useAtomValue(anyBlockingModalOpenAtom);
  const showOtherModals = useAtomValue(showOtherModalsAtom);

  // Decide whether to open. Re-evaluates when the gates settle (e.g. on
  // CartHistory once the WelcomeModal resolves, or after a blocking modal
  // closes). shouldShowAndRecord() gates re-showing via localStorage, so this
  // opens at most once per eligible window.
  useEffect(() => {
    // Turned off — never open (see DISABLED above).
    if (DISABLED) return;
    // Don't stack on top of a functional modal.
    if (anyBlockingModalOpen) return;
    // On CartHistory, let the WelcomeModal take precedence.
    if (deferToWelcomeModal && !showOtherModals) return;

    if (shouldShowAndRecord()) {
      setIsOpen(true);
    }
  }, [anyBlockingModalOpen, showOtherModals, deferToWelcomeModal, setIsOpen]);

  // If a functional modal opens while the nag is up, the nag yields (and stays
  // closed for the rest of the visit — it already counted as shown).
  useEffect(() => {
    if (anyBlockingModalOpen) {
      setIsOpen(false);
    }
  }, [anyBlockingModalOpen, setIsOpen]);
};
